import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Sun, Moon, AlertCircle, Loader2, CheckCircle2, Shield, Key } from 'lucide-react';
import { apiErrorMessage } from '../lib/apiErrors';

const Register = () => {
  const [searchParams] = useSearchParams();
  const codeFromUrl = new URLSearchParams(window.location.hash.slice(1)).get('code') || searchParams.get('code') || '';
  useEffect(() => {
    if (codeFromUrl) window.history.replaceState(null, '', window.location.pathname);
  }, [codeFromUrl]);
  
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    senha: '',
    confirmarSenha: '',
    codigoAutorizacao: codeFromUrl
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate authorization code
    if (!formData.codigoAutorizacao.trim()) {
      setError('Código de autorização é obrigatório');
      return;
    }

    if (formData.codigoAutorizacao.trim().length !== 12) {
      setError('Código de autorização deve ter 12 caracteres');
      return;
    }

    if (formData.senha !== formData.confirmarSenha) {
      setError('As senhas não coincidem');
      return;
    }

    if (formData.senha.length < 10 || new TextEncoder().encode(formData.senha).length > 72) {
      setError('A senha deve ter no mínimo 10 caracteres e no máximo 72 bytes');
      return;
    }

    setLoading(true);

    try {
      await register({
        nome: formData.nome,
        email: formData.email,
        senha: formData.senha,
        codigo_autorizacao: formData.codigoAutorizacao.toUpperCase().trim()
      });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(apiErrorMessage(err, 'Erro ao criar conta'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-muted/30 flex flex-col">
      {/* Header */}
      <header className="p-4 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <img src="/assets/logo-symbol.png" alt="LifePort" className="h-10 w-10 object-contain" />
          <div className="flex flex-col">
            <span className="font-bold text-xl font-['Manrope'] text-[hsl(var(--lifeport-red))]">LifePort</span>
            <span className="text-xs text-muted-foreground">Saúde no Lar</span>
          </div>
        </div>
        <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="btn-theme-toggle-register">
          {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </Button>
      </header>

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-lg">
          <CardHeader className="text-center space-y-2">
            <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-2">
              <Shield className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-2xl font-['Manrope']">Criar Conta</CardTitle>
            <CardDescription>
              Preencha os dados e o código de autorização para se cadastrar
            </CardDescription>
          </CardHeader>
          <CardContent>
            {success ? (
              <Alert className="border-green-500 bg-green-50 dark:bg-green-900/20" data-testid="register-success">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-700 dark:text-green-400">
                  Conta criada com sucesso! Redirecionando para o login...
                </AlertDescription>
              </Alert>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <Alert variant="destructive" data-testid="register-error">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                {/* Authorization Code Field - Required */}
                <div className="space-y-2">
                  <Label htmlFor="codigoAutorizacao" className="flex items-center gap-2">
                    <Key className="h-4 w-4 text-amber-600" />
                    Código de Autorização <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="codigoAutorizacao"
                    type="text"
                    placeholder="XXXXXXXXXXXX"
                    value={formData.codigoAutorizacao}
                    onChange={(e) => handleChange('codigoAutorizacao', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12))}
                    maxLength={12}
                    className="text-center text-lg tracking-widest font-mono uppercase"
                    required
                    data-testid="input-codigo-autorizacao"
                  />
                  <p className="text-xs text-muted-foreground">
                    Código de 12 caracteres fornecido pelo administrador de segurança
                  </p>
                </div>

                <div className="border-t pt-4">
                  <p className="text-sm font-medium text-muted-foreground mb-4">Dados do Usuário</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="nome">Nome Completo</Label>
                  <Input
                    id="nome"
                    type="text"
                    placeholder="Seu nome completo"
                    value={formData.nome}
                    onChange={(e) => handleChange('nome', e.target.value)}
                    required
                    data-testid="input-nome"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">E-mail</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="seu@email.com"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    required
                    data-testid="input-email-register"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="senha">Senha</Label>
                  <Input
                    id="senha"
                    type="password"
                    placeholder="Mínimo 10 caracteres"
                    maxLength={72}
                    autoComplete="new-password"
                    value={formData.senha}
                    onChange={(e) => handleChange('senha', e.target.value)}
                    required
                    data-testid="input-senha-register"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmarSenha">Confirmar Senha</Label>
                  <Input
                    id="confirmarSenha"
                    type="password"
                    placeholder="Repita a senha"
                    value={formData.confirmarSenha}
                    onChange={(e) => handleChange('confirmarSenha', e.target.value)}
                    required
                    data-testid="input-confirmar-senha"
                  />
                </div>

                <Alert className="bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800">
                  <Shield className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-700 dark:text-amber-400 text-sm">
                    Para obter um código de autorização, entre em contato com o administrador de segurança do sistema.
                  </AlertDescription>
                </Alert>

                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={loading}
                  data-testid="btn-register"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Criando conta...
                    </>
                  ) : (
                    <>
                      <Shield className="h-4 w-4 mr-2" />
                      Criar Conta
                    </>
                  )}
                </Button>
              </form>
            )}

            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground">
                Já tem uma conta?{' '}
                <Link 
                  to="/login" 
                  className="text-accent hover:underline font-medium"
                  data-testid="link-login"
                >
                  Faça login
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <footer className="p-4 text-center text-sm text-muted-foreground">
        © 2024 LifePort. Soluções em Saúde - Saúde no Lar.
      </footer>
    </div>
  );
};

export default Register;
